import { formatNumber } from "utils";

import type { ContributionMarginYearBreakdown } from "models";

import { CropYear, ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { categoryTotal, leafFromBreakdown } from "pages/app/scenario-analysis/utils/impactFactors";

// Values arrive from the run response already currency-converted and trend-adjusted — never re-apply
// the exchange rate or the market/cost trend here.
export interface DetailedCalculationRow {
  /** 0-based projection year index. */
  index: number;
  /** Index into the rotation, cycling when the projection outlasts the rotation. */
  rotationIndex: number;
  /** True where the rotation restarts (never on the first row). */
  isNewCycle: boolean;
  /** 1-based projection year as returned by the API. */
  year: number;
  /** Calendar year shown to the farmer, counted from when the baseline scenario was created. */
  calendarYear: number;
  cropId: string | null;
  /** Whether a cover crop was actually sown this calendar year, per the model — as opposed to merely
   * selected in the rotation input. Undefined, not false, when the API reports no sowing-event data at
   * all (older silver-service versions); only treat this as a real "not sown" signal when it is `false`. */
  coverCropSown?: boolean;
  /** True when this rotation slot has cover cropping selected, but the model explicitly reports nothing
   * was drilled this calendar year (`coverCropSown === false`) — a closed sowing window, for instance.
   * Always false when `coverCropSown` is unknown, so this never flags a false positive on an older
   * silver-service version. */
  coverCropMismatch: boolean;

  // Revenue
  yieldAmount: number;
  /** The model-adjusted yield the table shows. Null on a sowing year, which harvested nothing — distinct
   *  from a real 0.0, a modelled crop failure. `yieldAmount` stays the farmer's own figure because the
   *  revenue columns are billed on it; the uplift is already priced into the CM yield leaf. */
  adjustedYield: number | null;
  sellingPrice: number;
  /** Straw sold off the field; zero where the residues stay. Income, not part of `revenueTotal`. */
  strawRevenue: number;
  revenueTotal: number;
  lossesFodderPct: number;
  lossesMouldPct: number;
  revenueAdjusted: number;
  subsidies: number;

  // Costs
  seedCostsMain: number;
  seedCostsCover: number;
  fertiliserCosts: number;
  plantProtectionCosts: number;
  dieselCosts: number;
  machineryCosts: number;
  irrigationCosts?: number;
  /** "Other" collapsed total — post-harvest lines plus insurance. */
  otherCosts: number;
  cleaningCosts: number;
  /** Drying + storing + sorting; sorting has no column of its own. */
  dryingStorageCosts: number;
  insuranceCosts: number;
  /** Sum of the visible cost columns only: `variableTotal` plus cover seed, less land rental. */
  totalCosts: number;

  // Modelled impact
  yieldChangesImpact: number;
  costSavingsImpact: number;
  resilienceImpact: number;

  // Results
  cmMid: number;
  cmLow: number;
  cmHigh: number;
}

export interface DetailedCalculationTotals {
  strawRevenue: number;
  revenueAdjusted: number;
  subsidies: number;
  seedCostsMain: number;
  seedCostsCover: number;
  fertiliserCosts: number;
  plantProtectionCosts: number;
  dieselCosts: number;
  machineryCosts: number;
  irrigationCosts: number;
  otherCosts: number;
  cleaningCosts: number;
  dryingStorageCosts: number;
  insuranceCosts: number;
  totalCosts: number;
  yieldChangesImpact: number;
  costSavingsImpact: number;
  resilienceImpact: number;
  cmMid: number;
  /** Widest envelope across the projection: the per-year bands added up, which assumes the years
   *  move together. An upper bound on uncertainty rather than a statistical aggregate. */
  cmLow: number;
  cmHigh: number;
}

export interface ScenarioCalculationData {
  rows: DetailedCalculationRow[];
  /** Running CM total per row, aligned by index with `rows`. */
  cumulatives: number[];
  totals: DetailedCalculationTotals;
}

const ZERO_TOTALS: DetailedCalculationTotals = {
  strawRevenue: 0,
  revenueAdjusted: 0,
  subsidies: 0,
  seedCostsMain: 0,
  seedCostsCover: 0,
  fertiliserCosts: 0,
  plantProtectionCosts: 0,
  dieselCosts: 0,
  machineryCosts: 0,
  irrigationCosts: 0,
  otherCosts: 0,
  cleaningCosts: 0,
  dryingStorageCosts: 0,
  insuranceCosts: 0,
  totalCosts: 0,
  yieldChangesImpact: 0,
  costSavingsImpact: 0,
  resilienceImpact: 0,
  cmMid: 0,
  cmLow: 0,
  cmHigh: 0,
};

// Used when a run response carries no revenue/costs, so the table renders zeroes instead of throwing.
const ZERO_REVENUE: NonNullable<ContributionMarginYearBreakdown["revenue"]> = {
  yieldAmount: 0,
  sellingPrice: 0,
  lossesFodderShare: 0,
  lossesMouldShare: 0,
  lossesFodderPrice: 0,
  lossesMouldPrice: 0,
  revenueAdjusted: 0,
  strawRevenue: 0,
  subsidies: 0,
};

const ZERO_COSTS: NonNullable<ContributionMarginYearBreakdown["costs"]> = {
  seedsMain: 0,
  seedsCover: 0,
  fertiliser: 0,
  plantProtection: 0,
  diesel: 0,
  machinery: 0,
  cleaning: 0,
  dryingStorage: 0,
  insurance: 0,
  landRental: 0,
  variableTotal: 0,
};

const buildRow = (
  apiYear: ContributionMarginYearBreakdown,
  index: number,
  previousApiYear: ContributionMarginYearBreakdown | undefined,
  firstCalendarYear: number,
  cropYears: CropYear[]
): DetailedCalculationRow => {
  // Spread, not `??`: a response predating a field would otherwise put undefined on the row and NaN in the totals.
  const revenue = { ...ZERO_REVENUE, ...apiYear.revenue };
  const costs = { ...ZERO_COSTS, ...apiYear.costs };

  // `dryingStorage` already includes the engine's sorting line.
  const otherCosts = costs.cleaning + costs.dryingStorage + costs.insurance;

  const leaf = leafFromBreakdown(apiYear.contributionMargin?.breakdown);
  // The API returns a per-year interval; avg is the mid value shown.
  const cmBand = apiYear.contributionMargin?.total;
  const cmMid = cmBand?.avg ?? 0;

  // The api tracks the rotation positionally, so a repeated or wrapped crop is unambiguous.
  const rotationIndex = apiYear.rotationSlot;
  const isNewCycle = previousApiYear !== undefined && apiYear.cycleIndex > previousApiYear.cycleIndex;

  // The api already combines coverCropSown across a slot's blank-sowing-year + harvest-year group (see
  // assignCropYears), so the harvest row's own value answers for the whole group -- only harvest rows need
  // to be checked here. A blank row has no cropId to cross-check against a rotation edit (see
  // blankYearBreakdown), so it is deliberately excluded rather than trusted on rotationIndex alone: the
  // guard below requires the crop match unconditionally, same check buildCoverCropCostByYearId uses, so a
  // reordered/replaced slot never inherits a stale mismatch flag.
  const cropYearInput = cropYears[rotationIndex];
  const coverCropMismatch =
    apiYear.coverCropSown === false && cropYearInput !== undefined && cropYearInput.crop === apiYear.cropId && growsCoverCrop(cropYearInput);

  return {
    index,
    rotationIndex,
    isNewCycle,
    year: apiYear.year,
    calendarYear: firstCalendarYear + index,
    cropId: apiYear.cropId,
    coverCropSown: apiYear.coverCropSown,
    coverCropMismatch,

    yieldAmount: revenue.yieldAmount,
    adjustedYield: apiYear.cropId === null ? null : apiYear.adjustedCropMetrics?.cropYield?.avg ?? null,
    sellingPrice: revenue.sellingPrice,
    strawRevenue: revenue.strawRevenue,
    revenueTotal: revenue.yieldAmount * revenue.sellingPrice,
    // The API reports losses as shares; the table shows percentages.
    lossesFodderPct: revenue.lossesFodderShare * 100,
    lossesMouldPct: revenue.lossesMouldShare * 100,
    // Already net of both loss shares.
    revenueAdjusted: revenue.revenueAdjusted,
    subsidies: revenue.subsidies,

    seedCostsMain: costs.seedsMain,
    seedCostsCover: costs.seedsCover,
    fertiliserCosts: costs.fertiliser,
    plantProtectionCosts: costs.plantProtection,
    dieselCosts: costs.diesel,
    machineryCosts: costs.machinery,
    irrigationCosts: costs.irrigation,
    otherCosts,
    cleaningCosts: costs.cleaning,
    dryingStorageCosts: costs.dryingStorage,
    insuranceCosts: costs.insurance,
    // variableTotal carries landRental, which has no column here, and excludes cover seed, which does.
    // Both are corrected so the Total Costs cell always equals the columns beside it.
    totalCosts: costs.variableTotal + costs.seedsCover - costs.landRental,

    yieldChangesImpact: categoryTotal(leaf, "yield_quality"),
    costSavingsImpact: categoryTotal(leaf, "cost_savings"),
    resilienceImpact: categoryTotal(leaf, "resilience"),

    cmMid,
    cmLow: cmBand?.min ?? cmMid,
    cmHigh: cmBand?.max ?? cmMid,
  };
};

const TOTALLED_FIELDS = Object.keys(ZERO_TOTALS) as Array<keyof DetailedCalculationTotals>;

/** Rows, cumulatives and totals for one scenario. Memoise per scenario at the call site. */
export const buildScenarioCalculationData = (
  scenario: ScenarioData,
  outcomeYears: ContributionMarginYearBreakdown[] | undefined,
  firstCalendarYear: number
): ScenarioCalculationData => {
  const years = outcomeYears ?? [];
  const rows = years.map((apiYear, index) => buildRow(apiYear, index, years[index - 1], firstCalendarYear, scenario.years));

  let running = 0;
  const cumulatives = rows.map((row) => {
    running += row.cmMid;
    return running;
  });

  const totals = rows.reduce<DetailedCalculationTotals>(
    (acc, row) => {
      TOTALLED_FIELDS.forEach((field) => {
        acc[field] += row[field] ?? 0;
      });
      return acc;
    },
    { ...ZERO_TOTALS }
  );

  return { rows, cumulatives, totals };
};

/** Whole euros per hectare — the unit every money column in the Detailed calculations tab is shown in. */
export const formatPerHectare = (value: number): string => formatNumber({ number: value, decimalPlaces: 0 });

/** Year 1 of the projection, taken from when the baseline scenario was created. */
export const resolveFirstCalendarYear = (baselineCreatedAt: string | undefined): number => {
  const created = baselineCreatedAt ? new Date(baselineCreatedAt) : null;
  return created && !Number.isNaN(created.getTime()) ? created.getFullYear() : new Date().getFullYear();
};
